import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CALL_ROUTE } from '../components/empathy/callRoutes';
import { joinBooking } from '../services/booking.service';
import { useEmpathyQueueStore } from '../stores/empathy-queue.store';
import { withSession } from '../stores/session-read';
import type { Booking } from '../types/booking';
import { joinOpensAtMs, nextCountdownTickIn, nextJoinChangeIn } from '../utils/booking';

/**
 * How often a booking screen re-reads while it waits for the scheduler to
 * open the call. Only from POLL_LEAD_MS before the start (`awaitingSession`):
 * the rest of the ten-minute countdown runs on the clock alone, with no
 * requests.
 */
export const BOOKING_POLL_MS = 15_000;

/**
 * The re-reads begin this long before the start although the scheduler never
 * opens a call early: "the start" here is THIS device's clock. One running
 * slow would otherwise keep counting down while the other person waits in
 * the call, and five minutes slow would be counted a no-show before its Join
 * went live. Two minutes is the margin the old two-minute window gave. A
 * whole number of minutes, so the countdown's once-a-minute tick lands on it.
 */
export const POLL_LEAD_MS = 2 * 60 * 1000;

/**
 * Near or past the served `join_opens_at` with no session yet: the scheduler
 * is about to open the call.
 */
export function awaitingSession(
  booking: Pick<Booking, 'starts_at'> & Partial<Pick<Booking, 'join_opens_at'>>,
  state: string,
  now: number,
) {
  return state === 'starting' && now >= joinOpensAtMs(booking) - POLL_LEAD_MS;
}

const JOIN_FAILED = "Couldn't join the call. Try again.";

/**
 * Join a booked call (D32). The join route carries the 1:1 gates; the API
 * layer raises the circle agreement / "How empathy calls work" sheets on
 * their 403 and replays, so a success means both are satisfied. Then the
 * seat is read (the scheduler already put both people in the call, as a
 * match does) and the 1:1 page opens the call — reading it here because the
 * page may already be mounted (the list lives on it) and only reads on mount.
 */
export function useBookingJoin() {
  const navigate = useNavigate();
  const [joiningId, setJoiningId] = useState<number | null>(null);
  const [joinError, setJoinError] = useState<{
    id: number;
    message: string;
  } | null>(null);

  const join = useCallback(
    async (id: number): Promise<boolean> => {
      setJoiningId(id);
      setJoinError(null);
      const outcome = await withSession(() => joinBooking(id));
      if (outcome.status === 'ended') return false;
      if (outcome.status === 'ok' && outcome.value.success) {
        const store = useEmpathyQueueStore.getState();
        const read = await withSession(() =>
          outcome.value.success && outcome.value.data.role === 'giver'
            ? store.fetchGiverStatus()
            : store.fetchSeekerStatus(),
        );
        if (read.status === 'ended') return false;
        void navigate(CALL_ROUTE);
        return true;
      }
      setJoiningId(null);
      setJoinError({
        id,
        message:
          outcome.status === 'ok' && !outcome.value.success ? outcome.value.message : JOIN_FAILED,
      });
      return false;
    },
    [navigate],
  );

  return { join, joiningId, joinError };
}

/**
 * A clock that ticks when any of `bookings`' join state can change (start −
 * 10 min, the end) and, while one waits before its start, once a minute for
 * its "in N min": one timer, not a per-second interval.
 */
export function useJoinClock(
  bookings: Array<
    Pick<Booking, 'status' | 'starts_at' | 'ends_at'> &
      Partial<Pick<Booking, 'join_shows_at' | 'join_opens_at'>>
  >,
) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Measured from the real time, not `now`: every re-render with a new
    // array re-arms this timer, and a wait measured from the last tick would
    // be pushed out by each one (a 15 s re-read would stop the countdown).
    const at = Date.now();
    const waits = bookings
      .flatMap((b) => [nextJoinChangeIn(b, at), nextCountdownTickIn(b, at)])
      .filter((ms): ms is number => ms !== null);
    if (waits.length === 0) return;
    const id = window.setTimeout(() => setNow(Date.now()), Math.min(...waits));
    return () => window.clearTimeout(id);
  }, [bookings, now]);
  return now;
}
